import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { ExternalBankAccount } from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  PlaidAccount,
  PlaidLinkError,
  PlaidLinkOnExitMetadata,
  usePlaidLink
} from "react-plaid-link";
import { externalBankAccountsApi } from "@api";
import { queriesExternalBankAccounts } from "@query";
import { selectorPlaidAccountLinkable } from "@selectors/selectorPlaidAccountLinkable";
import { selectorPlaidAccountName } from "@selectors/selectorPlaidAccountName";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { useWSMutation } from "../../query/helpers";
import { analyticsService } from "@services/analyticsService";
import { WSServiceError } from "../../utils/serviceHelper";

type Config = {
  onSuccess: (externalBankAccounts: ExternalBankAccount[]) => void;
  onExit?: (
    error: PlaidLinkError | null,
    meta: PlaidLinkOnExitMetadata
  ) => void;
};

type ExchangeResult = {
  createdAccounts: ExternalBankAccount[];
  failedAccounts: PlaidAccount[];
};

/**
 * v3 Plaid link flow for external bank accounts: mints a link token
 * (`POST /external-bank-accounts/link`) on the first `open()` call and again
 * after every exit, opens Plaid Link, and exchanges the returned public token
 * for one external bank account per account the user selected in Link
 * (`POST /external-bank-accounts` with `publicToken` + `plaidAccountId` —
 * one create per selected account).
 *
 * Each create is independent, so one account the API refuses cannot discard
 * the accounts that did link: the failures are collected, named in a snackbar,
 * and `config.onSuccess` still runs with whatever was created. `onSuccess` is
 * skipped only when nothing at all was created.
 */
export const useExternalBankAccountsPlaidLink = (config: Config) => {
  const { openSnackbar } = useWSSnackbar();
  const [linkToken, setLinkToken] = useState<string>();
  const [handlerToken, setHandlerToken] = useState<string>();
  const [openPending, setOpenPending] = useState(false);
  const tokenRequestInFlightRef = useRef(false);

  const requestLinkToken = useCallback(() => {
    if (tokenRequestInFlightRef.current) {
      return;
    }
    tokenRequestInFlightRef.current = true;

    externalBankAccountsApi
      .createBankAccountLinkToken({ bankAccountLinkRequest: {} })
      .then(token => {
        setLinkToken(token.linkToken);
      })
      .catch(error => {
        console.error("Failed to create bank account link token", error);
        openSnackbar({
          type: "error",
          message: "Failed to start bank linking. Please try again."
        });
      })
      .finally(() => {
        tokenRequestInFlightRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A link token is spent once its Link session ends, and reopening the handler
  // it built does nothing at all (PLA-3624). Both endings — exit and success —
  // discard it, so the next `open()` mints a fresh token and a fresh handler.
  const discardLinkToken = useCallback(() => {
    setLinkToken(undefined);
    setOpenPending(false);
  }, []);

  const [exchangePublicToken, exchangePublicTokenMeta] = useWSMutation<
    ExchangeResult,
    WSServiceError,
    { publicToken: string; plaidAccounts: PlaidAccount[] }
  >(
    async ({ publicToken, plaidAccounts }) => {
      const createdAccounts: ExternalBankAccount[] = [];
      const failedAccounts: PlaidAccount[] = [];

      for (const plaidAccount of plaidAccounts) {
        const idempotencyKey = new IdempotencyKey({
          publicToken,
          plaidAccountId: plaidAccount.id
        });

        try {
          createdAccounts.push(
            await externalBankAccountsApi.createExternalBankAccount({
              idempotencyKey: idempotencyKey.value,
              externalBankAccountCreate: {
                publicToken,
                plaidAccountId: plaidAccount.id
              }
            })
          );

          idempotencyKey.destroy();
        } catch (error) {
          // The account is already linked at Plaid by this point, so aborting
          // the batch would leave the created accounts orphaned from the flow
          // with nothing to show the user (PLA-3555). Record the failure and
          // keep going. The key is kept so a replay of the same create is
          // deduped server-side.
          console.error(
            "Failed to create external bank account",
            plaidAccount.id,
            error
          );
          failedAccounts.push(plaidAccount);
        }
      }

      return { createdAccounts, failedAccounts };
    },
    {
      dependencies: [
        queriesExternalBankAccounts.listExternalBankAccounts.queryKeyBase
      ]
    }
  );

  const plaidLink = usePlaidLink({
    token: linkToken ?? null,
    onExit(error, meta) {
      discardLinkToken();

      if (error) {
        openSnackbar({
          type: "error",
          message:
            error.display_message ||
            `Plaid was unable to connect to ${
              meta.institution?.name ?? "your bank"
            }. Please try again.`
        });
      }

      config.onExit?.(error, meta);
    },
    async onSuccess(publicToken, metadata) {
      discardLinkToken();

      const linkableAccounts = metadata.accounts.filter(
        selectorPlaidAccountLinkable
      );
      const unsupportedAccounts = metadata.accounts.filter(
        plaidAccount => !selectorPlaidAccountLinkable(plaidAccount)
      );

      if (linkableAccounts.length === 0) {
        openSnackbar({
          type: "error",
          message:
            "No compatible accounts found. Only checking and savings accounts can be linked. Please try linking a different account."
        });
        return;
      }

      openSnackbar({
        message: "Linking accounts",
        icon: {
          name: "loader"
        }
      });

      const result = await exchangePublicToken({
        publicToken,
        plaidAccounts: linkableAccounts
      });

      // A rejected mutation resolves to `undefined`; treat it as "nothing linked".
      const createdAccounts = result?.createdAccounts ?? [];
      const notLinkedAccounts = [
        ...unsupportedAccounts,
        ...(result?.failedAccounts ?? linkableAccounts)
      ];

      if (createdAccounts.length === 0) {
        openSnackbar({
          type: "error",
          message: `Linking failed. Plaid was unable to successfully connect to ${
            metadata.institution?.name ?? "your bank"
          }. Please try again or contact member support.`
        });
        return;
      }

      if (notLinkedAccounts.length > 0) {
        const notLinkedNames = notLinkedAccounts
          .map(selectorPlaidAccountName)
          .join(", ");

        openSnackbar({
          type: "warning",
          message: `${notLinkedNames} could not be added. The other selected accounts were linked successfully.`
        });
      }

      config.onSuccess(createdAccounts);
    },
    onEvent(name: string, event: any) {
      analyticsService.track("Plaid Link Event Occurred", {
        eventName: name,
        ...event
      });

      // Plaid renders its iframe behind app modals unless bumped; mirror the
      // z-index workaround from the v1 hook.
      // https://github.com/plaid/react-plaid-link/issues/110
      switch (name) {
        case "OPEN": {
          window.document
            .querySelectorAll('[id^="plaid-link-iframe"]')
            .forEach(
              el => ((el as HTMLIFrameElement).style.zIndex = "2147483647")
            );
          break;
        }
        case "EXIT": {
          window.document
            .querySelectorAll('[id^="plaid-link-iframe"]')
            .forEach(el => ((el as HTMLIFrameElement).style.zIndex = "-999"));
          document.body.style.overflow = "";
          break;
        }
        default: {
        }
      }
    }
  });

  // `plaidLink.ready` stays true after a token is dropped — react-plaid-link
  // destroys the handler but keeps its own reference to it — so readiness alone
  // would open a dead handler. Track which token the live handler was built for;
  // this effect runs after the one that builds it, so a mismatch means the
  // handler for `linkToken` does not exist yet.
  useEffect(() => {
    setHandlerToken(linkToken);
  }, [linkToken]);

  const canOpen = plaidLink.ready && !!linkToken && handlerToken === linkToken;

  useEffect(() => {
    if (openPending && canOpen) {
      setOpenPending(false);
      plaidLink.open();
    }
  }, [openPending, canOpen, plaidLink]);

  const open = useCallback(() => {
    if (canOpen) {
      plaidLink.open();
    } else {
      requestLinkToken();
      setOpenPending(true);
    }
  }, [canOpen, plaidLink, requestLinkToken]);

  return {
    open,
    ready: canOpen,
    meta: exchangePublicTokenMeta
  };
};
